import { useEffect, useState } from "react";
import api from "@/lib/api";
import Visualizer from "@/components/Visualizer";
import {
  Users, CurrencyDollar, Crown, Pulse, Lightning, MusicNote, TrendDown,
  ArrowUpRight, ArrowDownRight, ShieldStar, FlowArrow, Fire, Trophy
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend
} from "recharts";

const TIER_COLORS = {
  standard: "#A1A1AA",
  intermediate: "#FF003C",
  pro: "#8B00FF",
};

export default function AdminOverview() {
  const [overview, setOverview] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null); // tier_key
  const [tiers, setTiers] = useState(null);
  const [users, setUsers] = useState([]);
  const [msg, setMsg] = useState("");
  const [revStreak, setRevStreak] = useState(null);
  const [streakBoard, setStreakBoard] = useState([]);

  const load = async () => {
    const [o, a, c, u, rs, sb] = await Promise.all([
      api.get("/admin/overview"),
      api.get("/admin/analytics?days=14"),
      api.get("/config"),
      api.get("/admin/users?limit=8"),
      api.get("/admin/streaks/revenue"),
      api.get("/admin/streaks/leaderboard?limit=6"),
    ]);
    setOverview(o.data);
    setAnalytics(a.data);
    setTiers(c.data.tiers);
    setUsers(u.data.items);
    setRevStreak(rs.data);
    setStreakBoard(sb.data);
  };

  useEffect(() => { load(); }, []);

  // Server-Sent Events for live stats — updates overview every 4s
  const [streaming, setStreaming] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem("bl_token");
    if (!token) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/overview/stream?token=${token}`;
    const es = new EventSource(url);
    es.onopen = () => setStreaming(true);
    es.onmessage = (ev) => {
      try { setOverview(JSON.parse(ev.data)); } catch {}
    };
    es.onerror = () => setStreaming(false);
    return () => es.close();
  }, []);

  if (!overview || !analytics || !tiers) {
    return <div className="p-10 text-ink-dim">LOADING CONTROL ROOM…</div>;
  }

  const tierData = Object.entries(overview.tier_distribution).map(([k, v]) => ({
    name: tiers[k]?.name || k.toUpperCase(),
    value: v,
    fill: TIER_COLORS[k],
  }));

  return (
    <div className="p-6 lg:p-8" data-testid="admin-overview">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red mb-2">◢ OVERVIEW</div>
          <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.9]">
            COMMAND <span className="text-brand-red neon-red">DECK.</span>
          </h1>
        </div>
        <button onClick={load} data-testid="overview-refresh" className="border border-zinc-700 px-4 py-2.5 text-[10px] tracking-[0.3em] uppercase hover:border-brand-red hover:text-brand-red inline-flex items-center gap-2">
          <span className={`w-1.5 h-1.5 rounded-full ${streaming ? "bg-brand-green animate-pulse" : "bg-zinc-700"}`} />
          {streaming ? "LIVE STREAM" : "REFRESH"} ▸
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-px bg-zinc-900 border border-zinc-900 mb-8">
        <Stat label="ACTIVE USERS" value={overview.total_users} delta="+12%" trend="up" Icon={Users} color="text-brand-red" testid="stat-users" />
        <Stat label="PLUGIN DOWNLOADS" value={overview.plugin_downloads.toLocaleString()} delta="+8%" trend="up" Icon={MusicNote} color="text-brand-purple" testid="stat-downloads" />
        <Stat label="MONTHLY REVENUE" value={overview.monthly_revenue_display} delta="+0.0%" trend="flat" Icon={CurrencyDollar} color="text-brand-green" testid="stat-revenue" />
        <Stat label="ACTIVE SUBS" value={overview.active_subscriptions} delta={`${overview.pending_subscriptions} pending`} trend="up" Icon={Crown} color="text-brand-red" testid="stat-subs" />
        <Stat label="TRIAL USERS" value={overview.trial_users} delta="conversion target" trend="flat" Icon={Lightning} color="text-brand-orange" testid="stat-trial" />
        <Stat label="LIVE SESSIONS" value={overview.live_sessions} delta="last 24h" trend="up" Icon={Pulse} color="text-brand-cyan" testid="stat-live" />
      </div>

      {/* Top row: revenue area + tier pie */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Panel title="REVENUE TRAJECTORY · 14D" tag="LIVE" className="lg:col-span-2" testid="revenue-chart">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={analytics.daily} margin={{ left: -10, right: 12, top: 12, bottom: 0 }}>
              <defs>
                <linearGradient id="gradRed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF003C" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#FF003C" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradPurple" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B00FF" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#8B00FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1a1a1a" vertical={false} />
              <XAxis dataKey="date" stroke="#52525b" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#52525b" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272A", fontFamily: "JetBrains Mono", fontSize: 11 }} labelStyle={{ color: "#71717A" }} />
              <Area type="monotone" dataKey="users" stroke="#FF003C" strokeWidth={2} fill="url(#gradRed)" />
              <Area type="monotone" dataKey="subscriptions" stroke="#8B00FF" strokeWidth={2} fill="url(#gradPurple)" />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-5 text-[10px] tracking-[0.3em] uppercase mt-2">
            <span className="flex items-center gap-2"><span className="w-2 h-2 bg-brand-red" /> New users</span>
            <span className="flex items-center gap-2"><span className="w-2 h-2 bg-brand-purple" /> Subscriptions</span>
          </div>
        </Panel>

        <Panel title="TIER DISTRIBUTION" tag={`${overview.active_subscriptions} ACTIVE`} testid="tier-pie">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={tierData} dataKey="value" innerRadius={55} outerRadius={95} stroke="#0a0a0a" strokeWidth={2} paddingAngle={2}>
                {tierData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272A", fontFamily: "JetBrains Mono", fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 mt-2">
            {tierData.map((d) => (
              <div key={d.name} className="flex items-center justify-between text-[10px] tracking-[0.25em] uppercase">
                <div className="flex items-center gap-2"><span className="w-2 h-2" style={{ background: d.fill }} /> {d.name}</div>
                <span className="text-white">{d.value}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Subscription management & Churn */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Panel title="SUBSCRIPTION TIERS · EDITABLE" tag="LIVE" className="lg:col-span-2" testid="tiers-editor">
          {msg && <div className="text-[10px] tracking-[0.3em] uppercase text-brand-green mb-3">{msg}</div>}
          <div className="grid sm:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            {["standard", "intermediate", "pro"].map((k) => {
              const t = tiers[k];
              const isEdit = editing === k;
              return (
                <div key={k} className="bg-bg-surface p-5" data-testid={`tier-card-${k}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-ink-dim">{t.name}</span>
                    <Lightning size={12} weight="fill" className="text-brand-red" />
                  </div>
                  {isEdit ? (
                    <PriceEditor
                      tier={k}
                      current={t}
                      onCancel={() => setEditing(null)}
                      onSaved={async (updated) => {
                        const next = { ...tiers, [k]: { ...t, ...updated } };
                        setTiers(next);
                        setEditing(null);
                        setMsg(`✓ ${t.name} updated`);
                        setTimeout(() => setMsg(""), 2500);
                      }}
                    />
                  ) : (
                    <>
                      <div className="font-display font-black tracking-tighter text-4xl">
                        {t.price_display}
                      </div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim mt-1">
                        {t.ai_credits_daily === 9999 ? "Unlimited AI" : `${t.ai_credits_daily} AI / day`}
                      </div>
                      <button
                        onClick={() => setEditing(k)}
                        data-testid={`tier-edit-${k}`}
                        className="mt-4 w-full border border-zinc-800 text-[10px] tracking-[0.3em] uppercase py-2 hover:border-brand-red hover:text-brand-red"
                      >
                        EDIT PRICING ▸
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel title="CHURN & RETENTION" tag="30D" testid="churn-panel">
          <div className="flex items-baseline gap-3 mb-4">
            <div className="font-display font-black text-5xl tracking-tighter text-brand-red">{overview.churn_rate}%</div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-ink-dim">churn rate</span>
          </div>
          <div className="space-y-3 mt-6">
            <Bar label="Retained" value={Math.max(0, 100 - overview.churn_rate)} color="#00FF66" />
            <Bar label="Churned" value={overview.churn_rate} color="#FF003C" />
            <Bar label="Trial → Paid" value={analytics.retention_pct || 0} color="#8B00FF" />
          </div>
          <div className="mt-5 pt-4 border-t border-zinc-900 text-[10px] tracking-[0.3em] uppercase text-ink-dim flex items-center justify-between">
            <span>Recurring payments</span>
            <span className="text-brand-green flex items-center gap-1"><ArrowUpRight size={12} weight="bold" /> stable</span>
          </div>
        </Panel>
      </div>

      {/* Gamification: revenue streak + producer leaderboard */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Panel title="REVENUE STREAK · DAYS" tag={revStreak?.is_on_fire ? "ON FIRE" : "TRACKING"} testid="revenue-streak-panel">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Fire
                size={64}
                weight={revStreak?.is_on_fire ? "fill" : "duotone"}
                className={revStreak?.is_on_fire ? "text-brand-red animate-pulse" : "text-zinc-700"}
              />
              {revStreak?.is_on_fire && (
                <div className="absolute inset-0 blur-2xl opacity-50 bg-brand-red rounded-full -z-10" />
              )}
            </div>
            <div className="flex-1">
              <div className="font-display font-black tracking-tighter text-6xl leading-none" data-testid="revenue-streak-value">
                {revStreak?.current_streak_days ?? 0}
              </div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim mt-1">
                CONSECUTIVE REVENUE DAYS
              </div>
            </div>
          </div>
          <div className="mt-5 pt-4 border-t border-zinc-900 grid grid-cols-2 gap-3 text-[10px] tracking-[0.25em] uppercase">
            <div>
              <div className="text-ink-dim">Longest</div>
              <div className="text-white font-mono text-lg">{revStreak?.longest_streak_days ?? 0}d</div>
            </div>
            <div>
              <div className="text-ink-dim">Next milestone</div>
              <div className="text-brand-red font-mono text-lg">{revStreak?.next_milestone ?? 3}d</div>
            </div>
          </div>
        </Panel>

        <Panel title="PRODUCER STREAK LEADERBOARD" tag="🔥 LIVE" className="lg:col-span-2" testid="streak-leaderboard">
          {streakBoard.length === 0 ? (
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim py-8 text-center">
              NO ACTIVE STREAKS YET — PRODUCERS BUILD THEIR FIRE BY LOGGING IN & GENERATING DAILY.
            </div>
          ) : (
            <div className="divide-y divide-zinc-900">
              {streakBoard.map((p, idx) => (
                <div
                  key={`${p.user_id}-${idx}`}
                  className="py-2.5 flex items-center gap-3"
                  data-testid={`streak-row-${idx}`}
                >
                  <div className="w-7 h-7 border border-zinc-800 bg-bg-surface flex items-center justify-center text-[10px] font-mono">
                    {idx === 0 ? <Trophy size={12} weight="fill" className="text-brand-red" /> : `#${idx + 1}`}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold uppercase tracking-tight text-sm truncate">
                      {p.producer_name}
                    </div>
                    <div className="text-[10px] text-ink-dim">
                      Longest: {p.longest_streak}d · Last: {p.last_active_date || "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] font-mono">
                    <Fire size={14} weight={p.is_on_fire ? "fill" : "regular"} className={p.is_on_fire ? "text-brand-red" : "text-zinc-700"} />
                    <span className={p.is_on_fire ? "text-brand-red" : "text-ink-mute"}>{p.current_streak}d</span>
                  </div>
                  <span className={`text-[9px] tracking-[0.25em] uppercase px-1.5 py-0.5 border ${
                    p.tier === "pro" ? "border-brand-purple text-brand-purple"
                    : p.tier === "intermediate" ? "border-brand-red text-brand-red"
                    : "border-zinc-700 text-ink-mute"
                  }`}>
                    {p.tier || "FREE"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Recent producers + Audio reactive panel */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="RECENT PRODUCERS" tag={`${users.length} OF ${overview.total_users}`} className="lg:col-span-2" testid="recent-users">
          <div className="divide-y divide-zinc-900">
            {users.map((u) => (
              <div key={u.id} className="py-3 flex items-center gap-3" data-testid={`user-row-${u.id}`}>
                <div className="w-8 h-8 border border-zinc-800 bg-bg-surface flex items-center justify-center text-[10px] tracking-[0.2em] uppercase">
                  {(u.producer_name || "?").slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold uppercase tracking-tight text-sm truncate">
                    {u.producer_name} {u.is_admin && <span className="ml-2 text-[9px] tracking-[0.3em] text-brand-red border border-brand-red px-1.5 py-0.5">ADMIN</span>}
                  </div>
                  <div className="text-[10px] text-ink-dim truncate">{u.email}</div>
                </div>
                <span className={`text-[10px] tracking-[0.25em] uppercase px-2 py-1 border ${
                  u.tier === "pro" ? "border-brand-purple text-brand-purple"
                  : u.tier === "intermediate" ? "border-brand-red text-brand-red"
                  : u.tier === "standard" ? "border-zinc-700 text-ink-mute"
                  : "border-zinc-800 text-ink-dim"
                }`}>
                  {u.tier || "FREE"}
                </span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="SIGNAL FLOW" tag="LIVE" testid="signal-flow">
          <Visualizer height={180} style="bars" />
          <div className="mt-4 grid grid-cols-3 gap-px bg-zinc-900">
            {[
              ["LATENCY", "0.04s"],
              ["CPU", "12%"],
              ["NODES", overview.total_users + overview.live_sessions]
            ].map(([l, v]) => (
              <div key={l} className="bg-bg-surface p-3 text-center">
                <div className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">{l}</div>
                <div className="font-display text-sm font-bold mt-1">{v}</div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Stat({ label, value, delta, trend, Icon, color, testid }) {
  const TrendIcon = trend === "up" ? ArrowUpRight : trend === "down" ? ArrowDownRight : FlowArrow;
  const trendColor = trend === "up" ? "text-brand-green" : trend === "down" ? "text-brand-red" : "text-ink-dim";
  return (
    <div className="bg-bg-surface p-5 group hover:bg-bg-surface2 transition-colors relative" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <Icon size={16} weight="bold" className={color} />
        <span className={`text-[9px] tracking-[0.2em] uppercase flex items-center gap-1 ${trendColor}`}>
          <TrendIcon size={10} weight="bold" /> {delta}
        </span>
      </div>
      <div className="font-display font-black text-3xl tracking-tighter">{value}</div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim mt-1">{label}</div>
      <div className="absolute top-0 right-0 w-6 h-6 border-r border-t border-current opacity-0 group-hover:opacity-30 transition-opacity"
        style={{ color: "#FF003C" }} />
    </div>
  );
}

function Panel({ title, tag, children, className = "", testid }) {
  return (
    <div className={`border border-zinc-800 bg-bg-surface grain ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900">
        <span className="text-[10px] tracking-[0.3em] uppercase text-ink-dim flex items-center gap-2">
          <ShieldStar size={11} weight="bold" className="text-brand-red" /> {title}
        </span>
        {tag && <span className="text-[9px] tracking-[0.3em] uppercase text-brand-red animate-flicker">● {tag}</span>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Bar({ label, value, color }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] tracking-[0.25em] uppercase">
        <span className="text-ink-dim">{label}</span>
        <span className="text-white font-mono">{value.toFixed(1)}%</span>
      </div>
      <div className="h-1.5 bg-zinc-900 mt-1.5">
        <div className="h-full transition-all" style={{ width: `${Math.min(100, value)}%`, background: color, boxShadow: `0 0 10px ${color}80` }} />
      </div>
    </div>
  );
}

function PriceEditor({ tier, current, onCancel, onSaved }) {
  const [price, setPrice] = useState((current.price_cents / 100).toFixed(2));
  const [credits, setCredits] = useState(current.ai_credits_daily);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/tiers/${tier}`, {
        price_cents: Math.round(parseFloat(price) * 100),
        ai_credits_daily: parseInt(credits, 10),
      });
      onSaved(data);
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <label className="block">
        <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">$ / month</span>
        <input value={price} onChange={(e) => setPrice(e.target.value)} data-testid={`tier-input-price-${tier}`}
          className="mt-1 w-full bg-black border border-zinc-800 px-2 py-1.5 text-sm font-mono outline-none focus:border-brand-red" />
      </label>
      <label className="block">
        <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">AI / day</span>
        <input value={credits} onChange={(e) => setCredits(e.target.value)} data-testid={`tier-input-credits-${tier}`}
          className="mt-1 w-full bg-black border border-zinc-800 px-2 py-1.5 text-sm font-mono outline-none focus:border-brand-red" />
      </label>
      <div className="flex gap-1.5 pt-1">
        <button onClick={save} disabled={busy} data-testid={`tier-save-${tier}`} className="flex-1 bg-brand-red text-black text-[10px] tracking-[0.25em] uppercase font-bold py-2 hover:bg-white disabled:opacity-50">
          {busy ? "..." : "SAVE"}
        </button>
        <button onClick={onCancel} className="flex-1 border border-zinc-700 text-[10px] tracking-[0.25em] uppercase py-2 hover:border-brand-red">
          CANCEL
        </button>
      </div>
    </div>
  );
}
